import { z } from 'zod';

import { MAX_PAGE_SIZE } from '../../common/pagination/pagination';
import { ProductKeySchema } from '../product-key/product-key';

/**
 * A set of product **keys** that arrives as ONE comma-joined query parameter.
 *
 * `IdsParamSchema` is the same idea and cannot be reused: it pipes into
 * `z.array(UuidSchema)`, and a plan is addressed everywhere outside the
 * catalogue by its key — `direct_payment_orders.product_key`, a discount rule's
 * `appliesToProductKeys`, every entry in `billing.directPrices`. A picker that
 * can only resolve uuids can therefore offer plans and never name the one a row
 * already holds, which is the gap this exists to close (W6, and the alternatives
 * it forbids by name: an N+1 of detail calls, or a `pageSize=100` preload).
 *
 * It is a **string**, not `z.array(ProductKeySchema)`, for the reason
 * `IdsParamSchema` is one: Express's `qs` parses a single `?keys=x` into a bare
 * string and two into an array, so an array-shaped schema would accept the
 * one-element request only by accident. One spelling, no ambiguity (N7).
 *
 * Capped at `MAX_PAGE_SIZE` so a `keys` filter can never out-run the page it is
 * resolving against — the same ceiling, refused rather than clamped.
 */
export const ProductKeysParamSchema = z
  .string()
  .transform((raw) =>
    raw
      .split(',')
      .map((segment) => segment.trim())
      .filter((segment) => segment.length > 0),
  )
  .pipe(z.array(ProductKeySchema).min(1).max(MAX_PAGE_SIZE));

export type ProductKeysParam = z.infer<typeof ProductKeysParamSchema>;
